import type { EditorConfig } from '@ckeditor/ckeditor5-core';
import { Alignment } from '@ckeditor/ckeditor5-alignment';
import {
  Bold,
  Code,
  Italic,
  Strikethrough,
  Underline,
} from '@ckeditor/ckeditor5-basic-styles';
import { BlockQuote } from '@ckeditor/ckeditor5-block-quote';
import { Essentials } from '@ckeditor/ckeditor5-essentials';
import {
  Heading,
  HeadingButtonsUI,
} from '@ckeditor/ckeditor5-heading';
import {
  AutoImage,
  Image,
  ImageCaption,
  ImageInsert,
  ImageResize,
  ImageStyle,
  ImageToolbar,
} from '@ckeditor/ckeditor5-image';
import {
  Indent,
  IndentBlock,
} from '@ckeditor/ckeditor5-indent';
import {
  AutoLink,
  Link,
  LinkImage,
} from '@ckeditor/ckeditor5-link';
import { List } from '@ckeditor/ckeditor5-list';
import { MediaEmbed } from '@ckeditor/ckeditor5-media-embed';
import {
  Paragraph,
  ParagraphButtonUI,
} from '@ckeditor/ckeditor5-paragraph';
import { RemoveFormat } from '@ckeditor/ckeditor5-remove-format';
import { SourceEditing } from '@ckeditor/ckeditor5-source-editing';
import { Base64UploadAdapter } from '@ckeditor/ckeditor5-upload';
import { WordCount } from '@ckeditor/ckeditor5-word-count';

import VariablesPlugin from './VariablesPlugin';

export interface CKEditorConfigOptions {
  onCharactersUpdate(options: { characters: number; words: number }): void;
}

export default function getCKEditorDefaultConfig({ onCharactersUpdate }: CKEditorConfigOptions): EditorConfig {
  return {
    plugins: [
      Indent,
      IndentBlock,
      Essentials,
      Bold,
      Italic,
      Paragraph,
      Heading,
      HeadingButtonsUI,
      ParagraphButtonUI,
      Code,
      BlockQuote,
      Underline,
      Strikethrough,
      List,
      RemoveFormat,
      Alignment,
      Image,
      AutoImage,
      ImageInsert,
      ImageResize,
      ImageStyle,
      ImageCaption,
      AutoLink,
      LinkImage,
      Link,
      Base64UploadAdapter,
      ImageToolbar,
      MediaEmbed,
      SourceEditing,
      WordCount,
      VariablesPlugin,
    ],

    toolbar: {
      items: [
        'heading1',
        'heading2',
        'heading3',
        'heading4',
        'heading5',
        'heading6',
        'paragraph',
        'code',
        'blockquote',
        '|',
        'bold',
        'italic',
        'underline',
        'strikethrough',
        'bulletedList',
        'numberedList',
        'undo',
        'redo',
        'removeFormat',
        '|',
        'alignment:left',
        'alignment:center',
        'alignment:right',
        'alignment:justify',
        'outdent',
        'indent',
        '|',
        'link',
        'insertImage',
        'mediaEmbed',
        '|',
        'variables',
        '|',
        'sourceEditing',
      ],
      shouldNotGroupWhenFull: true,
    },

    image: {
      toolbar: [
        'imageStyle:block',
        'imageStyle:inline',
        'imageStyle:side',
        '|',
        'toggleImageCaption',
        'imageTextAlternative',
        '|',
        'linkImage',
      ],
    },

    heading: {
      options: [
        { model: 'paragraph', title: 'Paragraph', class: '' },
        {
          model: 'heading1',
          view: 'h1',
          title: 'Heading 1',
          class: '',
        },
        {
          model: 'heading2',
          view: 'h2',
          title: 'Heading 2',
          class: '',
        },
        {
          model: 'heading3',
          view: 'h3',
          title: 'Heading 3',
          class: '',
        },
        {
          model: 'heading4',
          view: 'h4',
          title: 'Heading 4',
          class: '',
        },
        {
          model: 'heading5',
          view: 'h5',
          title: 'Heading 5',
          class: '',
        },
        {
          model: 'heading6',
          view: 'h6',
          title: 'Heading 6',
          class: '',
        },
      ],
    },
    wordCount: {
      onUpdate: onCharactersUpdate,
    },
  };
}
